import {
  parseExpireMs,
  resolveDeviceExpireTime,
  THIRTY_DAYS_MS
} from 'src/utils/subscriptionExpire'

/** 设备订阅状态机（与后端 user-device.subscription_status 对齐） */
export const SUBSCRIPTION_STATUS = Object.freeze({
  /** 未过期 */
  ACTIVE: 'active',
  /** 已过期且 ≤30 天（含第 30 天，宽限期，仍可选中） */
  GRACE: 'grace',
  /** 已过期且 >30 天（失效，禁止选中） */
  LAPSED: 'lapsed',
  /**
   * 特殊态：无已支付订单，或 expire_time 为空/异常。
   * ≠ lapsed：不进「全员超 30 天」独立续费页；也不可选中。
   */
  NONE: 'none'
})

const STATUS_SET = new Set(Object.values(SUBSCRIPTION_STATUS))

export const normalizeSubscriptionStatus = raw => {
  if (raw == null || raw === '') return null
  const status = String(raw).toLowerCase()

  return STATUS_SET.has(status) ? status : null
}

/**
 * 由 expire_time 本地兜底（接口未回 subscription_status 时用）。
 * 无有效 expire_time → active（兼容历史无到期字段仍可进 dashboard）。
 * 服务端显式 none 时走 subscription_status 字段，不走本函数。
 */
export const resolveSubscriptionStatus = (expireTime, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const ms = parseExpireMs(expireTime)
  if (!Number.isFinite(ms)) return SUBSCRIPTION_STATUS.ACTIVE
  if (ms >= now) return SUBSCRIPTION_STATUS.ACTIVE
  if (now - ms <= graceMs) return SUBSCRIPTION_STATUS.GRACE

  return SUBSCRIPTION_STATUS.LAPSED
}

/**
 * 优先用设备/DeviceUser 上的 subscription_status；否则用 expire_time 兜底。
 */
export const resolveDeviceSubscriptionStatus = (device, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const fromApi = normalizeSubscriptionStatus(device?.subscription_status)
  if (fromApi) return fromApi

  return resolveSubscriptionStatus(resolveDeviceExpireTime(device) ?? device?.expire_time, now, graceMs)
}

/** active / grace 可选中；lapsed / none 禁止成为当前选中 */
export const isSelectableSubscriptionStatus = status =>
  status === SUBSCRIPTION_STATUS.ACTIVE || status === SUBSCRIPTION_STATUS.GRACE

export const isSelectableDevice = (device, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  if (!device) return false

  return isSelectableSubscriptionStatus(resolveDeviceSubscriptionStatus(device, now, graceMs))
}

export const isLapsedDevice = (device, now = Date.now(), graceMs = THIRTY_DAYS_MS) =>
  resolveDeviceSubscriptionStatus(device, now, graceMs) === SUBSCRIPTION_STATUS.LAPSED

export const isGraceDevice = (device, now = Date.now(), graceMs = THIRTY_DAYS_MS) =>
  resolveDeviceSubscriptionStatus(device, now, graceMs) === SUBSCRIPTION_STATUS.GRACE

export const isNoneDevice = (device, now = Date.now(), graceMs = THIRTY_DAYS_MS) =>
  resolveDeviceSubscriptionStatus(device, now, graceMs) === SUBSCRIPTION_STATUS.NONE

/**
 * 侧栏设备卡片是否展示：
 * - active / grace：展示
 * - none：展示（特殊：无有效已付单，但仍是绑定记录）
 * - lapsed：隐藏
 */
export const isDeviceVisibleInSidebar = (device, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  if (!device) return false

  return resolveDeviceSubscriptionStatus(device, now, graceMs) !== SUBSCRIPTION_STATUS.LAPSED
}

/**
 * 设备关联已支付订单 ID（后端 user-device.order_id）。
 * @returns {string|number|null}
 */
export const resolveDeviceOrderId = device => {
  if (!device) return null
  const id = device.order_id ?? device.orderId ?? device?.Order?.id ?? device?.order?.id
  if (id == null || id === '') return null

  return id
}

/**
 * 初始化/恢复选中：优先 active，其次 grace；永不返回 lapsed/none。
 * @param {Array} devices
 * @param {string} [platformType]
 */
export const pickPreferredSelectableDevice = (devices, platformType) => {
  const list = Array.isArray(devices) ? devices : []
  const scoped = platformType ? list.filter(item => item?.platform_type === platformType) : list
  const selectable = scoped.filter(item => isSelectableDevice(item))
  if (!selectable.length) return null

  const active = selectable.find(
    item => resolveDeviceSubscriptionStatus(item) === SUBSCRIPTION_STATUS.ACTIVE
  )

  return active || selectable[0] || null
}

/**
 * 选中校验：仅 active/grace 可选。
 * @returns {{ ok: boolean, status: string, reason?: string }}
 */
export const validateDeviceSelection = (device, now = Date.now()) => {
  if (!device || (typeof device === 'object' && !Object.keys(device).length)) {
    return { ok: true, status: SUBSCRIPTION_STATUS.NONE }
  }

  const status = resolveDeviceSubscriptionStatus(device, now)
  if (!isSelectableSubscriptionStatus(status)) {
    return {
      ok: false,
      status,
      reason:
        status === SUBSCRIPTION_STATUS.LAPSED
          ? 'lapsed_device_not_selectable'
          : 'device_not_selectable'
    }
  }

  return { ok: true, status }
}

/** 侧栏不可选设备的英文提示（none ≠ 过期） */
export const getDeviceNotSelectableMessage = (device, now = Date.now()) => {
  const status = resolveDeviceSubscriptionStatus(device, now)

  if (status === SUBSCRIPTION_STATUS.NONE) {
    return 'This package has no active subscription and cannot be selected.'
  }

  if (status === SUBSCRIPTION_STATUS.LAPSED) {
    return 'This package subscription has expired and cannot be selected.'
  }

  return 'This package cannot be selected.'
}

/**
 * 是否「全部设备都是 lapsed」。
 * none 不算 lapsed（有 none 混在时为 false）。
 * 只信 subscription_status（无字段时才 expire_time 兜底）。
 */
export const areAllDevicesLapsed = (devices, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const list = Array.isArray(devices) ? devices : []
  if (!list.length) return false

  return list.every(device => isLapsedDevice(device, now, graceMs))
}

/** 无任何可选中设备（全是 lapsed / none） */
export const areAllDevicesNonSelectable = (devices, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const list = Array.isArray(devices) ? devices : []
  if (!list.length) return false

  return list.every(device => !isSelectableDevice(device, now, graceMs))
}

/**
 * 独立续费页入口（替换旧「全设备过期超 30 天 + 无未绑单」本地推算）：
 * - 设备非空且全部 subscription_status === lapsed
 * - 且 not-bind-order 无有效未绑单
 *
 * none 不进此页。
 */
export const shouldEnterExpiredPurchasePage = ({ devices = [], validUnboundOrders = [] } = {}) => {
  const list = Array.isArray(devices) ? devices : []
  const unbound = Array.isArray(validUnboundOrders) ? validUnboundOrders : []

  return list.length > 0 && areAllDevicesLapsed(list) && unbound.length === 0
}

/**
 * 登录/进站：active/grace 设备 > 未绑单 > 空态
 * 「全部过期超 30 天」的判定已上移到 userEntryGate（后端状态优先），这里不再参与。
 * 永不选中 lapsed/none。
 * @returns {{ type: 'device'|'unbound'|'empty', device: object|null }}
 */
export const resolveInitialEntry = ({
  devices = [],
  validUnboundOrders = [],
  preferredMode = null,
  preferredDevice = null
} = {}) => {
  const list = Array.isArray(devices) ? devices : []
  const unbound = Array.isArray(validUnboundOrders) ? validUnboundOrders : []

  if (preferredDevice && isSelectableDevice(preferredDevice)) {
    return { type: 'device', device: preferredDevice }
  }

  const preferred =
    pickPreferredSelectableDevice(list, preferredMode) || pickPreferredSelectableDevice(list)

  if (preferred) {
    return { type: 'device', device: preferred }
  }

  if (unbound.length > 0) {
    return { type: 'unbound', device: null }
  }

  return { type: 'empty', device: null }
}
